import { Box, Paper, Typography } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeImageEditDialog } from "components/ReportNodeImage/ReportNodeImageEditDialog"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTableEditDialog } from "components/ReportNodeTable/ReportNodeTableEditDialog"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { ReportNodeTextEditDialog } from "components/ReportNodeText/ReportNodeTextEditDialog"
import { Criteria, CriteriaCatalog, DataNode, NodeDataType } from "graphql/types"
import { useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { v4 } from "uuid"
import { CriteriaList } from "./CriteriaList"

type CriteriaCatalogContentProps = {
  criteriaCatalog: CriteriaCatalog
  onUpdateCriteriaCatalog: (updatedCatalog: CriteriaCatalog) => void
  operatingModes: string[]
}

export const CriteriaCatalogContent = (props: CriteriaCatalogContentProps) => {
  const { criteriaCatalog, onUpdateCriteriaCatalog, operatingModes } = props

  const [newNode, setNewNode] = useState<DataNode>()

  // const handleChapterNeededChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   onUpdateCriteriaCatalog({
  //     ...criteriaCatalog,
  //     chapterNeeded: event.target.checked,
  //   })
  // }

  const handleOpenAddNode = (type: NodeDataType) => {
    const newNode: DataNode = {
      id: v4(),
      dataType: type,
      editable: true,
      deletable: true,
      image: [],
      imageCaption: [],
      imageNumber: [],
      tableBody: [],
      tableCaption: "",
      tableHeader: [],
      tableNumber: 1,
      text: "",
    }
    setNewNode(newNode)
  }

  const handleCloseAddNode = () => {
    setNewNode(undefined)
  }

  const handleAddNode = () => {
    if (newNode) {
      const isOldNode = criteriaCatalog.description.find((item) => item.id === newNode?.id)
      if (isOldNode) {
        onUpdateCriteriaCatalog({
          ...criteriaCatalog,
          description: criteriaCatalog.description.map((item) =>
            item.id === newNode?.id ? newNode : item
          ),
        })
      } else {
        onUpdateCriteriaCatalog({
          ...criteriaCatalog,
          description: [...criteriaCatalog.description, newNode],
        })
      }
      handleCloseAddNode()
    }
  }

  const handleDeleteNode = (id: string) => {
    onUpdateCriteriaCatalog({
      ...criteriaCatalog,
      description: criteriaCatalog.description.filter((item) => item.id !== id),
    })
  }

  const handleEditNode = (node: DataNode) => {
    setNewNode(node)
  }

  const handleDeleteCriteria = (index: number) => {
    onUpdateCriteriaCatalog({
      ...criteriaCatalog,
      criteriaList: criteriaCatalog.criteriaList.filter((_, i) => i !== index),
    })
  }

  const handleSaveCriteria = (criteria: Criteria) => {
    const existingIndex = criteriaCatalog.criteriaList.findIndex((c) => c.id === criteria.id)
    if (existingIndex >= 0) {
      // Update existing criteria
      onUpdateCriteriaCatalog({
        ...criteriaCatalog,
        criteriaList: criteriaCatalog.criteriaList.map((c) =>
          c.id === criteria.id ? criteria : c
        ),
      })
    } else {
      // Add new criteria
      onUpdateCriteriaCatalog({
        ...criteriaCatalog,
        criteriaList: [...criteriaCatalog.criteriaList, criteria],
      })
    }
  }

  return (
    <>
      {/* <FormControlLabel
        control={
          <Switch
            checked={criteriaCatalog.chapterNeeded || false}
            onChange={handleChapterNeededChange}
          />
        }
        label="Soll im Testreport ein eigenes Kapitel 2.6 mit dem Namen dieses Kriteriums angelegt werden?"
        sx={{ mt: 2 }}
      /> */}

      <Paper sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Beschreibung</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <StyledButton
              buttonType="add"
              customText="Text"
              size="small"
              onClick={() => handleOpenAddNode(NodeDataType.TEXT)}
            />
            <StyledButton
              buttonType="add"
              customText="Tabelle"
              size="small"
              onClick={() => handleOpenAddNode(NodeDataType.TABLE)}
            />
            <StyledButton
              buttonType="add"
              customText="Bild"
              size="small"
              onClick={() => handleOpenAddNode(NodeDataType.IMAGE)}
            />
          </Box>
        </Box>
        {criteriaCatalog.description.length > 0 ? (
          <Box>
            {criteriaCatalog.description.map((item) => (
              <Box
                key={item.id}
                sx={{
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                {isNodeText(item) && <ReportNodeTextBox data={item} />}
                {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                {isNodeImage(item) && <ReportNodeImageBox data={item} />}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <StyledButton
                    buttonType="edit"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleEditNode(item)}
                  />
                  <StyledButton
                    buttonType="delete"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleDeleteNode(item.id)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Keine Beschreibung hinzugefügt. Verwenden Sie die Buttons oben, um Inhalte hinzuzufügen.
          </Typography>
        )}
      </Paper>

      <CriteriaList
        criteriaList={criteriaCatalog.criteriaList}
        onSaveCriteria={handleSaveCriteria}
        onDeleteCriteria={handleDeleteCriteria}
        operatingModes={operatingModes}
      />

      {/* DataNode Edit Dialogs */}
      {newNode && isNodeText(newNode) && (
        <ReportNodeTextEditDialog
          open={newNode.dataType === NodeDataType.TEXT}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeTable(newNode) && (
        <ReportNodeTableEditDialog
          open={newNode.dataType === NodeDataType.TABLE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeImage(newNode) && (
        <ReportNodeImageEditDialog
          open={newNode.dataType === NodeDataType.IMAGE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}
    </>
  )
}
